'use client';

import { c as _c } from "react/compiler-runtime";
import type { AddPrCommentType } from '@/components/inbox/prs/types/pr-comment-input';
import { Play } from 'lucide-react';
import { toast } from 'sonner';
import { useEventCallback } from 'usehooks-ts';
import { Button } from '../../ui/button';
import { Shimmer } from '@/components/ai-elements/shimmer';
function Header(t0) {
  const $ = _c(12);
  const {
    args,
    applyAiComment
  } = t0;
  let t1;
  if ($[0] !== args.filename) {
    t1 = args.filename.split("/").pop();
    $[0] = args.filename;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shortFilename = t1;
  const t2 = `Apply suggestion for: ${args.filename}:${args.lineNumber}`;
  const t3 = `${shortFilename}:${args.lineNumber}`;
  let t4;
  if ($[2] !== applyAiComment || $[3] !== t2 || $[4] !== t3) {
    t4 = <button type="button" onClick={applyAiComment} className="cursor-pointer text-zinc-400 hover:text-white focus:outline-none" title={t2}>{t3}</button>;
    $[2] = applyAiComment;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Play size={16} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== applyAiComment) {
    t6 = <div className="flex flex-row gap-2"><Button variant="ghost" size="tiny" className="bg-transparent transition-colors hover:bg-zinc-950 hover:text-primary" onClick={applyAiComment} title="Apply comment">{t5}</Button></div>;
    $[7] = applyAiComment;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <div className="w-full bg-zinc-800 px-2 py-1 text-xs text-muted-foreground"><div className="flex w-full flex-row items-center justify-between gap-2">{t4}{t6}</div></div>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

/**
 * Renders a chat message block when an AI comment has been added.
 *
 * This component is used when the AI has successfully added a comment to a PR.
 * It shows the comment that was added, and provides controls to copy the comment
 * or apply it to the PR.
 */
export function AiAddedPrCommentBlock(t0) {
  const $ = _c(13);
  const {
    args,
    applyAiComment
  } = t0;
  let t1;
  if ($[0] !== applyAiComment || $[1] !== args.comment || $[2] !== args.filename || $[3] !== args.lineNumber) {
    t1 = () => {
      if (!args.filename || !args.lineNumber || !args.comment) {
        toast.error("Missing required information to apply comment");
        return;
      }
      applyAiComment();
    };
    $[0] = applyAiComment;
    $[1] = args.comment;
    $[2] = args.filename;
    $[3] = args.lineNumber;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleApplyComment = useEventCallback(t1);
  let t2;
  if ($[5] !== args || $[6] !== handleApplyComment) {
    t2 = <Header args={args} applyAiComment={handleApplyComment} />;
    $[5] = args;
    $[6] = handleApplyComment;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== args.comment) {
    t3 = <div className="bg-zinc-900 p-2 text-xs">{args.comment}</div>;
    $[8] = args.comment;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <div className="mt-2 flex flex-col gap-0 overflow-hidden rounded-md">{t2}{t3}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
/**
 * Renders a chat message block when an AI comment is being added.
 */
export function AiAddingPrCommentBlock() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mt-2 flex flex-col gap-0 overflow-hidden rounded-md"><div className="w-full bg-zinc-800 px-2 py-1 text-xs text-muted-foreground"><Shimmer>Adding PR comment...</Shimmer></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}